import { Card, type CardProps } from '@mui/material';
import React from 'react';
import { P, match } from 'ts-pattern';
import TablerIconChevronDown from '~icons/tabler/chevron-down';
import TablerIconChevronUp from '~icons/tabler/chevron-up';
import { cn, styleVars } from '#/utils/styles';

export interface CollapseCardProps extends Omit<CardProps, 'onToggle'> {
  /**
   * The height of the card when collapsed.
   * @default 100
   */
  collapsedHeight?: number;
  /**
   * The icon to display when the card is collapsed.
   */
  expandIcon?: React.ReactNode;
  /**
   * The icon to display when the card is expanded.
   */
  collapseIcon?: React.ReactNode;
  /**
   * Whether the collapse button is disabled.
   */
  disabled?: boolean;
  /**
   * Whether the card is collapsed by default.
   * @default false
   */
  defaultCollapsed?: boolean;
  /**
   * The function to call when the card is toggled.
   */
  onToggle?: ({ prev, next }: { prev: boolean; next: boolean }) => void;
}

/**
 * A card that can be collapsed and expanded.
 * @param props - The props for the card.
 * @returns A card that can be collapsed and expanded.
 */
function CollapseCard({
  children,
  className,
  expandIcon,
  collapseIcon,
  collapsedHeight = 100,
  disabled = false,
  defaultCollapsed = false,
  onToggle,
  ...props
}: CollapseCardProps) {
  const [open, setOpen] = React.useState(!defaultCollapsed);

  const expandIconNode = React.useMemo(() => {
    return match(expandIcon)
      .with(P.nullish, () => <TablerIconChevronDown />)
      .otherwise(() => expandIcon);
  }, [expandIcon]);

  const collapseIconNode = React.useMemo(() => {
    return match(collapseIcon)
      .with(P.nullish, () => <TablerIconChevronUp />)
      .otherwise(() => collapseIcon);
  }, [collapseIcon]);

  return (
    <Card
      {...props}
      className={cn('relative', className, {
        'h-(--collapse-card-collapsed-height)': !open,
      })}
      style={styleVars({
        '--collapse-card-collapsed-height': `${collapsedHeight}px`,
      })}
    >
      {children}
      <div className='absolute bottom-0 inset-x-0'>
        {!open && (
          <div className='h-16 bg-gradient-to-t from-background-paper to-transparent' />
        )}
        <div className='group relative w-full cursor-pointer'>
          <div className='absolute inset-0 z-0 group-hover:bg-background-default' />
          <button
            type='button'
            disabled={disabled}
            className='reset w-full relative z-10 flex flex-row items-center justify-center bg-background-paper group-hover:bg-background-paper/70'
            onClick={() => {
              if (disabled) return;
              const next = !open;
              setOpen(next);
              onToggle?.({ prev: open, next });
            }}
          >
            {match(open)
              .with(true, () => collapseIconNode)
              .with(false, () => expandIconNode)
              .exhaustive()}
          </button>
        </div>
      </div>
    </Card>
  );
}

export default CollapseCard;
